/**
 * React Router v4 → v7 Compatibility Layer
 * 
 * This module provides compatibility wrappers to ease migration from 
 * React Router v4 to v7. It handles the key API differences:
 * - `component` prop → `element` prop
 * - `render` prop → `element` prop with inline JSX
 * - Automatic route props (match, location, history) → hooks
 * - history.block() → custom blocking implementation
 * 
 * Usage: Replace existing Route components with CompatRoute
 * After migration is complete, refactor to native v7 Routes.
 */
import React, { createContext, useContext, useRef, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Route,
  useParams,
  useLocation,
  useNavigate,
  UNSAFE_NavigationContext as NavigationContext,
} from "react-router-dom";

// Context to share blocking functionality with class components
const BlockerContext = createContext(null);

// Marker we put on history.state to identify a sentinel entry that we own.
// When a blocker is active, we push a duplicate of the current history entry
// tagged with this key so that the browser's back button lands on a known
// "shadow" of the current page rather than actually navigating away. See
// BlockerProvider for details.
const BLOCKER_STATE_KEY = "__navigationBlocker";

/**
 * Extract the pathname from the various call signatures of navigator.push / navigator.replace / navigate().
 * React Router's internal navigator accepts either a string path, a partial Path object, or a To descriptor.
 */
function extractPathname(to) {
  if (typeof to === "string") {
    // Strip query/hash for pathname comparison
    const queryIdx = to.indexOf("?");
    const hashIdx = to.indexOf("#");
    let end = to.length;
    if (queryIdx !== -1) end = Math.min(end, queryIdx);
    if (hashIdx !== -1) end = Math.min(end, hashIdx);
    return to.slice(0, end);
  }
  if (to && typeof to === "object" && typeof to.pathname === "string") {
    return to.pathname;
  }
  return null;
}

/**
 * BlockerProvider - Provides navigation blocking functionality to child components.
 *
 * Implementation detail: React Router v7's <Link> component calls the navigator
 * obtained from UNSAFE_NavigationContext directly, which bypasses any wrapper
 * around useNavigate(). To block <Link> clicks (e.g. sidebar links) we have to
 * intercept at the NavigationContext level by providing a wrapped navigator
 * whose push/replace methods consult the registered blocker callback first.
 *
 * Components using useNavigate() (including our withRouter shim) automatically
 * pick up the wrapped navigator through the same context, so a single override
 * covers both code paths.
 *
 * Browser back/forward (and programmatic history.go) bypass the navigator
 * entirely - the browser fires `popstate` directly. To block those we use a
 * sentinel-state pattern: when a blocker registers we push a duplicate history
 * entry (same URL) tagged with BLOCKER_STATE_KEY. Pressing back lands on the
 * original entry without changing the URL, so React Router doesn't unmount the
 * blocked page. We catch the popstate, prompt, and either re-push the sentinel
 * (cancel) or call history.go(-1) to actually leave (confirm).
 */
export function BlockerProvider({ children }) {
  const blockerCallbacksRef = useRef(new Set());
  const locationRef = useRef(null);
  const sentinelPushedRef = useRef(false);
  // Set when we trigger a popstate ourselves (e.g. cleaning up the sentinel
  // on unregister). Lets the popstate handler ignore that one event so it
  // doesn't re-prompt the user for a navigation we initiated.
  const ignoreNextPopRef = useRef(false);
  const location = useLocation();
  const parentNavigationContext = useContext(NavigationContext);

  locationRef.current = location;

  const pushSentinelState = useCallback(() => {
    if (sentinelPushedRef.current) return;
    const currentState = window.history.state;
    // If the current entry is already a sentinel (e.g. we just got back here
    // after the user cancelled), just remember that fact - don't add another.
    if (currentState && currentState[BLOCKER_STATE_KEY]) {
      sentinelPushedRef.current = true;
      return;
    }
    // Preserve any existing state fields (React Router stores idx/key/usr) so
    // its internal index tracking stays sane.
    const newState = { ...(currentState || {}), [BLOCKER_STATE_KEY]: true };
    window.history.pushState(newState, "", window.location.href);
    sentinelPushedRef.current = true;
  }, []);

  const popSentinelState = useCallback(() => {
    if (!sentinelPushedRef.current) return;
    sentinelPushedRef.current = false;
    const currentState = window.history.state;
    // Only collapse the sentinel if we're currently sitting on it. If the
    // user navigated away (push/replace) the sentinel is buried below other
    // entries and we can't safely remove it - leave it as a harmless duplicate.
    if (currentState && currentState[BLOCKER_STATE_KEY]) {
      ignoreNextPopRef.current = true;
      window.history.go(-1);
    }
  }, []);

  const getBlockMessage = useCallback((nextLocation) => {
    const callbacks = Array.from(blockerCallbacksRef.current);
    // Newer blockers first so deeply nested/most-recent registrations get
    // first chance to veto navigation.
    for (let i = callbacks.length - 1; i >= 0; i -= 1) {
      const message = callbacks[i]?.(nextLocation);
      if (message) return message;
    }
    return undefined;
  }, []);

  const confirmNavigation = useCallback((to) => {
    if (blockerCallbacksRef.current.size === 0) return true;

    const targetPathname = extractPathname(to);
    if (!targetPathname) return true;
    if (targetPathname === locationRef.current?.pathname) return true;

    const message = getBlockMessage({ pathname: targetPathname });
    if (!message) return true;
    return window.confirm(message);
  }, [getBlockMessage]);

  // Wrap the router's navigator so that <Link> clicks and programmatic
  // navigate() calls both go through the blocker.
  const wrappedNavigationContextValue = useMemo(() => {
    const originalNavigator = parentNavigationContext.navigator;
    const wrappedNavigator = {
      ...originalNavigator,
      push: (to, state, opts) => {
        if (!confirmNavigation(to)) return;
        return originalNavigator.push(to, state, opts);
      },
      replace: (to, state, opts) => {
        if (!confirmNavigation(to)) return;
        return originalNavigator.replace(to, state, opts);
      },
      // go() (history.goBack/forward, navigate(-1), etc.) ultimately fires a
      // popstate, which is handled by the listener below. Pass through here.
      go: (...args) => originalNavigator.go?.(...args),
    };
    return { ...parentNavigationContext, navigator: wrappedNavigator };
  }, [parentNavigationContext, confirmNavigation]);

  // Also block full page unloads / refreshes / closing tab when a blocker is registered.
  useEffect(() => {
    const handler = (e) => {
      if (blockerCallbacksRef.current.size > 0) {
        e.preventDefault();
        // Modern browsers ignore custom messages but require returnValue to be set
        e.returnValue = "";
        return "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  // Intercept browser back/forward (and programmatic history.go). When the
  // user lands on the natural entry from a sentinel (event.state has no
  // BLOCKER_STATE_KEY) and a blocker is active, prompt them. The URL hasn't
  // changed (sentinel and natural entry share a URL), so React Router doesn't
  // unmount the page - the form's local state is preserved during the prompt.
  useEffect(() => {
    const handler = (event) => {
      if (ignoreNextPopRef.current) {
        ignoreNextPopRef.current = false;
        return;
      }

      if (blockerCallbacksRef.current.size === 0) {
        // Mild UX cleanup: if the user navigated away via push/replace while a
        // sentinel existed, that sentinel can be left behind in history.
        // Landing on it later (back from another page) is a duplicate entry
        // with no active blocker. Auto-skip it so users don't need an extra
        // manual back press.
        if (event.state && event.state[BLOCKER_STATE_KEY]) {
          ignoreNextPopRef.current = true;
          window.history.go(-1);
        }
        return;
      }

      // Still on a sentinel - the user navigated forward into one, or another
      // sentinel is on top. Nothing to block.
      if (event.state && event.state[BLOCKER_STATE_KEY]) return;

      // The sentinel was just popped. Mark it as gone so a cancel will
      // correctly push a new one.
      sentinelPushedRef.current = false;

      // Pass an empty pathname so consumers comparing `current !== target`
      // always get a "different page" signal - we can't know the real target
      // for a back/forward pop and the user IS leaving the current page.
      const message = getBlockMessage({ pathname: "" });
      if (!message) return;

      if (window.confirm(message)) {
        // User confirmed - clear the blocker and actually pop one more entry
        // to honour the back navigation. Mark the resulting popstate as
        // internal so we don't try to prompt again on the way out.
        ignoreNextPopRef.current = true;
        window.history.go(-1);
        return;
      }

      // User cancelled - re-push the sentinel so the next back press prompts
      // again. URL doesn't change, so no React Router re-render is needed.
      pushSentinelState();
    };

    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [pushSentinelState, getBlockMessage]);

  const block = useCallback((callback) => {
    if (!callback) return () => {};
    const hadNoBlockers = blockerCallbacksRef.current.size === 0;
    blockerCallbacksRef.current.add(callback);
    if (hadNoBlockers) {
      pushSentinelState();
    }
    return () => {
      if (blockerCallbacksRef.current.has(callback)) {
        blockerCallbacksRef.current.delete(callback);
      }
      if (blockerCallbacksRef.current.size === 0) {
        popSentinelState();
      }
    };
  }, [pushSentinelState, popSentinelState]);

  // Memoize so consumers' effects that depend on the context value don't
  // re-run on every render of BlockerProvider.
  const blockerContextValue = useMemo(
    () => ({ block }),
    [block]
  );

  return (
    <NavigationContext.Provider value={wrappedNavigationContextValue}>
      <BlockerContext.Provider value={blockerContextValue}>
        {children}
      </BlockerContext.Provider>
    </NavigationContext.Provider>
  );
}

BlockerProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * useBlockerContext - Hook to access blocker functionality
 */
export function useBlockerContext() {
  return useContext(BlockerContext);
}

/**
 * ComponentWrapper - Wraps a v4-style component to provide router props via hooks
 */
function ComponentWrapper({ component: Component, ...rest }) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const blockerContext = useBlockerContext();
  
  const history = {
    push: (pathOrLocation, state) => {
      if (typeof pathOrLocation === 'object' && pathOrLocation !== null) {
        const { pathname, search, hash, state: locationState } = pathOrLocation;
        const path = pathname + (search || '') + (hash || '');
        navigate(path, { state: locationState });
      } else {
        navigate(pathOrLocation, { state });
      }
    },
    replace: (pathOrLocation, state) => {
      if (typeof pathOrLocation === 'object' && pathOrLocation !== null) {
        const { pathname, search, hash, state: locationState } = pathOrLocation;
        const path = pathname + (search || '') + (hash || '');
        navigate(path, { replace: true, state: locationState });
      } else {
        navigate(pathOrLocation, { replace: true, state });
      }
    },
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    block: blockerContext?.block || (() => () => {}),
    location,
  };
  
  const match = {
    params,
    path: location.pathname,
    url: location.pathname,
  };
  
  return <Component {...rest} match={match} location={location} history={history} navigate={navigate} />;
}

ComponentWrapper.propTypes = {
  component: PropTypes.elementType,
};

/**
 * RenderWrapper - Wraps a v4-style render prop to provide router props via hooks
 */
function RenderWrapper({ render, ...rest }) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const blockerContext = useBlockerContext();
  
  const history = {
    push: (pathOrLocation, state) => {
      if (typeof pathOrLocation === 'object' && pathOrLocation !== null) {
        const { pathname, search, hash, state: locationState } = pathOrLocation;
        const path = pathname + (search || '') + (hash || '');
        navigate(path, { state: locationState });
      } else {
        navigate(pathOrLocation, { state });
      }
    },
    replace: (pathOrLocation, state) => {
      if (typeof pathOrLocation === 'object' && pathOrLocation !== null) {
        const { pathname, search, hash, state: locationState } = pathOrLocation;
        const path = pathname + (search || '') + (hash || '');
        navigate(path, { replace: true, state: locationState });
      } else {
        navigate(pathOrLocation, { replace: true, state });
      }
    },
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    block: blockerContext?.block || (() => () => {}),
    location,
  };
  
  const match = {
    params,
    path: location.pathname,
    url: location.pathname,
  };
  
  return render({ ...rest, match, location, history, navigate });
}

/**
 * convertRouteToV7 - Converts a v4 Route element to v7 format
 * Used by convertRoutesToV7 to process route arrays
 */
export function convertRouteToV7(v4Route, index) {
  if (!v4Route || !v4Route.props) return null;
  
  const { path, exact, component: Component, render, children, ...otherProps } = v4Route.props;
  const key = v4Route.key || `route-${index}`;
  
  // In v7, all routes are exact by default. Use path="something/*" for non-exact matching
  let v7Path = path;
  if (path && !exact && path !== "/" && !path.endsWith("*")) {
    v7Path = `${path}/*`;
  }
  
  let element;
  if (Component) {
    element = <ComponentWrapper component={Component} {...otherProps} />;
  } else if (render) {
    element = <RenderWrapper render={render} {...otherProps} />;
  } else if (children) {
    element = children;
  }
  
  // Preserve original props (like topMenuTitle, rootCssClass) on the Route element
  // so CoreLayout can access them for header display
  return <Route key={key} path={v7Path || "*"} element={element} {...otherProps} />;
}

/**
 * convertRoutesToV7 - Converts an array of v4 Route elements to v7 format
 * Use this when you have an array of routes from AuthorizedRoutes
 */
export function convertRoutesToV7(v4Routes) {
  if (!Array.isArray(v4Routes)) {
    return convertRouteToV7(v4Routes, 0);
  }
  return v4Routes.map((route, index) => convertRouteToV7(route, index));
}

export default { convertRouteToV7, convertRoutesToV7, BlockerProvider, useBlockerContext };
